import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@powersync/react';
import { useApp, useClinicCtx } from '@/app/state';
import { Button, Card, Empty, Pill, cx } from '@/components/ui';
import { callNext, setSessionStatus, setTokenStatus, type TokenStatus } from '@/features/queue/repo';
import type { SessionRow, TokenRow } from '@/lib/db/schema';
import { formatTime, toMyanmarDigits } from '@/lib/myanmar/digits';

type S = SessionRow & { doctor_name_en: string | null; doctor_name_my: string | null };
type T = TokenRow & { name_en: string | null; name_my: string | null; patient_no: string | null; honorific: string | null };

const TONE: Record<TokenStatus, 'neutral' | 'accent' | 'warn' | 'danger'> = { waiting: 'neutral', called: 'warn', in_consultation: 'accent', done: 'accent', skipped: 'danger', no_show: 'danger' };

export function QueuePage() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { hasRole } = useApp();
  const nav = useNavigate();
  const { data: sessions } = useQuery<S>(`select s.*, d.name_en as doctor_name_en, d.name_my as doctor_name_my from sessions s join doctors d on d.id = s.doctor_id where s.clinic_id = ? and s.session_date = date('now','localtime') and s.status <> 'cancelled' order by s.start_time`, [ctx.clinicId]);
  const [selected, setSelected] = useState<string | null>(null);
  const active = (sessions ?? []).find((s) => s.id === selected) ?? sessions?.[0] ?? null;
  const { data: tokens } = useQuery<T>(
    `select t.*, i.name_en, i.name_my, i.patient_no, i.honorific from tokens t left join patient_index i on i.patient_id = t.patient_id where t.session_id = ? order by case t.status when 'in_consultation' then 0 when 'called' then 1 when 'waiting' then 2 else 3 end, t.priority desc, t.issued_at`,
    [active?.id ?? '']);

  if ((sessions ?? []).length === 0) return <Empty>{t('queue.noSessions')} <Link className="underline" to="/settings">{t('nav.settings')}</Link></Empty>;
  const current = (tokens ?? []).find((x) => x.status === 'in_consultation') ?? (tokens ?? []).find((x) => x.status === 'called');
  const waiting = (tokens ?? []).filter((x) => x.status === 'waiting');
  const name = (x: T) => `${x.honorific && x.honorific !== 'none' ? t(`honorific.${x.honorific}`) + ' ' : ''}${my ? (x.name_my ?? x.name_en ?? '') : (x.name_en ?? x.name_my ?? '')}`;
  const label = (l: string) => (my ? toMyanmarDigits(l) : l);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-2">
        {(sessions ?? []).map((s) => (
          <button key={s.id} onClick={() => setSelected(s.id)} className={cx('rounded-xl border px-4 py-2 text-left', active?.id === s.id ? 'border-accent bg-accent-soft' : 'border-rule bg-surface')}>
            <div className="font-bold">{my ? (s.doctor_name_my ?? s.doctor_name_en) : (s.doctor_name_en ?? s.doctor_name_my)}</div>
            <div className="text-xs text-ink-2">{s.start_time}–{s.end_time} · {t(`session.status.${s.status}`)}</div>
          </button>
        ))}
        <Link to="/board" target="_blank" className="ml-auto self-center text-sm text-accent-ink underline">{t('nav.board')} ↗</Link>
      </div>

      {active && (
        <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
          <Card className="grid gap-3">
            <div className="text-sm font-semibold uppercase tracking-wide text-muted">{t('queue.nowServing')}</div>
            {current ? (
              <div>
                <div className="text-6xl font-bold leading-none">{label(current.token_label ?? '')}</div>
                <Link to={`/patients/${current.patient_id}`} className="mt-2 block text-xl font-bold hover:underline">{name(current)}</Link>
                <div className="text-ink-2">{current.patient_no}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(current.status === 'called' || current.status === 'in_consultation') && hasRole('owner', 'doctor', 'nurse') && (
                    <Button variant="primary" onClick={() => nav(`/consult/${current.id}`)}>{current.status === 'called' ? t('consult.openConsult') : t('consult.resume')}</Button>
                  )}
                  {current.status === 'in_consultation' && <Button onClick={() => setTokenStatus(ctx, current.id, 'done')}>{t('queue.finish')}</Button>}
                  <Button onClick={() => setTokenStatus(ctx, current.id, 'skipped')}>{t('queue.skip')}</Button>
                  {current.status === 'called' && <Button variant="danger" onClick={() => setTokenStatus(ctx, current.id, 'no_show')}>{t('queue.no_show')}</Button>}
                </div>
              </div>
            ) : <div className="text-muted">{waiting.length > 0 ? t('queue.nobodyCalled') : t('queue.empty')}</div>}
            <Button variant="primary" disabled={waiting.length === 0 || (!!current && current.status === 'in_consultation') || active.status !== 'open'} onClick={() => callNext(ctx, active.id)} className="text-lg">
              {t('queue.callNext')} {waiting.length > 0 && `· ${label(waiting[0]!.token_label ?? '')}`}
            </Button>
            {hasRole('owner', 'receptionist', 'doctor') && (
              <div className="flex gap-2 border-t border-rule pt-3">
                {active.status !== 'open' && active.status !== 'closed' && <Button onClick={() => setSessionStatus(ctx, active.id, 'open')}>{t('queue.openSession')}</Button>}
                {active.status === 'open' && <Button onClick={() => setSessionStatus(ctx, active.id, 'closed')}>{t('queue.closeSession')}</Button>}
              </div>
            )}
          </Card>

          <div className="grid gap-2">
            {(tokens ?? []).length === 0 && <Empty>{t('queue.empty')}</Empty>}
            {(tokens ?? []).map((x) => (
              <div key={x.id} className={cx('flex items-center gap-3 rounded-xl border border-rule bg-surface px-4 py-2', x.status === 'done' && 'opacity-60')}>
                <div className="w-16 text-2xl font-bold">{label(x.token_label ?? '')}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{name(x)} {x.priority ? <Pill tone="warn">{t('queue.priority')}</Pill> : null}</div>
                  <div className="text-xs text-ink-2">{x.patient_no} · {formatTime(x.issued_at, my ? 'my' : 'en')}</div>
                </div>
                <Pill tone={TONE[(x.status ?? 'waiting') as TokenStatus]}>{t(`queue.${x.status}`)}</Pill>
                {(x.status === 'skipped' || x.status === 'no_show') && <Button onClick={() => setTokenStatus(ctx, x.id, 'waiting')} className="py-1">{t('queue.requeue')}</Button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
